import {
  ActionIcon,
  Button,
  Card,
  Group,
  Input,
  SegmentedControl,
  Select,
  Stack,
  Table,
} from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import { PageTitle } from 'components';
import { useProfile } from 'init/use-enforce-profile';
import { i18nKeys, useTranslation } from 'locales';
import { useState } from 'react';
import { useAddGroupMember } from 'shared/hooks/use-add-group-member';
import { useChangeGroupMemberRole } from 'shared/hooks/use-change-group-member-role';
import { useLoadGroupCandidates } from 'shared/hooks/use-load-group-candidates';
import { type GroupMember, useLoadGroupMembers } from 'shared/hooks/use-load-group-members';
import { useRemoveGroupMember } from 'shared/hooks/use-remove-group-member';

const ROLE_KEYS: Record<GroupMember['role'], string> = {
  admin: i18nKeys.GROUP.ROLES.ADMIN,
  member: i18nKeys.GROUP.ROLES.MEMBER,
};

export const GroupMembers = () => {
  const { t } = useTranslation();
  const { groupMembers } = useLoadGroupMembers();
  const { removeGroupMember, isRemoveGroupMemberLoading } = useRemoveGroupMember();
  const { changeGroupMemberRole } = useChangeGroupMemberRole();
  const { id: currentUserId } = useProfile();
  const administersGroup = groupMembers.some(
    member => member.id === currentUserId && member.role === 'admin',
  );
  const { groupCandidates } = useLoadGroupCandidates({ enabled: administersGroup });
  const { addGroupMember, isAddGroupMemberLoading } = useAddGroupMember();
  const [pickedCandidateId, setPickedCandidateId] = useState<string | null>(null);
  const [pickedRole, setPickedRole] = useState<GroupMember['role']>('member');
  const roleChoices: Array<{ value: GroupMember['role']; label: string }> = [
    { value: 'member', label: t(ROLE_KEYS.member) },
    { value: 'admin', label: t(ROLE_KEYS.admin) },
  ];

  const addPickedCandidate = () =>
    addGroupMember(
      { userId: Number(pickedCandidateId), role: pickedRole },
      {
        onSuccess: () => {
          setPickedCandidateId(null);
          setPickedRole('member');
        },
      },
    );

  return (
    <Stack mih="100%" gap={0}>
      <PageTitle>{t(i18nKeys.GROUP.MEMBERS)}</PageTitle>
      {administersGroup && (
        <Group align="flex-end" gap="sm" py="md">
          <Select
            label={t(i18nKeys.GROUP.MEMBER_TO_ADD)}
            data={groupCandidates.map(({ id, firstName, lastName }) => ({
              value: String(id),
              label: `${firstName} ${lastName}`,
            }))}
            value={pickedCandidateId}
            onChange={setPickedCandidateId}
          />
          <Input.Wrapper label={t(i18nKeys.GROUP.ROLE)} labelElement="div">
            <SegmentedControl
              aria-label={t(i18nKeys.GROUP.ROLE)}
              value={pickedRole}
              onChange={setPickedRole}
              data={roleChoices}
            />
          </Input.Wrapper>
          <Button
            disabled={!pickedCandidateId || isAddGroupMemberLoading}
            onClick={addPickedCandidate}
          >
            {t(i18nKeys.GROUP.ADD_MEMBER)}
          </Button>
        </Group>
      )}
      <Card radius={8} p={0}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t(i18nKeys.NAME)}</Table.Th>
              <Table.Th>{t(i18nKeys.EMAIL)}</Table.Th>
              <Table.Th>{t(i18nKeys.GROUP.ROLE)}</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {groupMembers.map(({ id, firstName, lastName, email, role }) => (
              <Table.Tr key={id}>
                <Table.Td>{`${firstName} ${lastName}`}</Table.Td>
                <Table.Td>{email}</Table.Td>
                <Table.Td>
                  {administersGroup ? (
                    <SegmentedControl
                      size="xs"
                      value={role}
                      onChange={nextRole => changeGroupMemberRole({ id, role: nextRole })}
                      data={roleChoices}
                    />
                  ) : (
                    t(ROLE_KEYS[role])
                  )}
                </Table.Td>
                <Table.Td>
                  {administersGroup && (
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      disabled={isRemoveGroupMemberLoading}
                      onClick={() => removeGroupMember({ id })}
                      aria-label={t(i18nKeys.GROUP.REMOVE_MEMBER, {
                        name: `${firstName} ${lastName}`,
                      })}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>
    </Stack>
  );
};
